import React, { useState } from 'react';
import PlayerHand from './PlayerHand';
import FaceUpCards from './FaceUpCards';

/**
 * Swap Phase overlay.
 * Shown when game.status === 'swap'.
 *
 * Rules:
 *   - Player picks one hand card (give) and one faceUp card (take)
 *   - Clicking Swap exchanges them (can do this multiple times before ready)
 *   - Clicking Ready marks the player as done; when all players are ready, game starts
 *
 * Props:
 *   myState     — { hand, faceUp, swapReady }
 *   onSwap      — (give, take) => void
 *   onReady     — () => void
 *   readyCount  — number of players who clicked ready
 *   totalCount  — total number of players
 */
const SwapPhase = ({
  myState,
  onSwap,
  onReady,
  readyCount = 0,
  totalCount = 0,
  waitingOnNames = [],
  onBackToWaitingRoom,
  onCollapseInactive,
  onVoteCollapse,
  canManageWaitingRoom = false,
  collapsingInactive = false,
  collapseProposal = null,
  myUserId = '',
}) => {
  const isSeatedPlayer = !!myState;
  const [selectedHand, setSelectedHand] = useState(null);
  const [selectedFaceUp, setSelectedFaceUp] = useState(null);

  const canSwap = selectedHand && selectedFaceUp;
  const myVoteRecorded = !!(collapseProposal?.yesUserIds || []).some((uid) => uid?.toString?.() === myUserId?.toString?.());

  const collapsePanel = collapseProposal ? (
    <div className="waiting-room-panel" style={{ marginTop: '0.6rem' }}>
      <p className="admin-note">
        Collapse vote started by {collapseProposal.requestedByName || 'a player'}.
      </p>
      <p className="swap-ready-count">
        Yes votes: {(collapseProposal.yesUserIds || []).length}
      </p>
      <div className="swap-actions" style={{ marginTop: '0.4rem' }}>
        <button
          onClick={() => onVoteCollapse?.(true)}
          className="btn-play"
          type="button"
          disabled={myVoteRecorded || collapsingInactive}
        >
          {myVoteRecorded ? 'Vote Recorded' : 'Approve Collapse + Redeal'}
        </button>
      </div>
    </div>
  ) : null;

  const handleSwap = () => {
    if (!canSwap) return;
    onSwap(selectedHand, selectedFaceUp);
    setSelectedHand(null);
    setSelectedFaceUp(null);
  };

  if (!isSeatedPlayer) {
    return (
      <div className="swap-phase">
        <h2>Swap Phase</h2>
        <p className="swap-waiting">
          Waiting for seated players… ({readyCount}/{totalCount} ready)
        </p>
        {waitingOnNames.length > 0 && (
          <p className="admin-note">Waiting on: {waitingOnNames.join(', ')}</p>
        )}
        <div className="swap-actions" style={{ marginTop: '0.65rem' }}>
          <button onClick={onBackToWaitingRoom} className="btn-cancel" type="button">
            Back To Waiting Room
          </button>
          {canManageWaitingRoom && (
            <button onClick={onCollapseInactive} className="btn-cancel" type="button" disabled={collapsingInactive}>
              {collapsingInactive ? 'Sending...' : 'Propose Collapse + Redeal'}
            </button>
          )}
        </div>
        {collapsePanel}
      </div>
    );
  }

  if (myState?.swapReady) {
    return (
      <div className="swap-phase">
        <h2>Swap Phase</h2>
        <p className="swap-waiting">
          Waiting for others… ({readyCount}/{totalCount} ready)
        </p>
        {waitingOnNames.length > 0 && (
          <p className="admin-note">Waiting on: {waitingOnNames.join(', ')}</p>
        )}
        <div className="swap-actions" style={{ marginTop: '0.65rem' }}>
          <button onClick={onBackToWaitingRoom} className="btn-cancel" type="button">
            Back To Waiting Room (Keep Seat)
          </button>
          {canManageWaitingRoom && (
            <button onClick={onCollapseInactive} className="btn-cancel" type="button" disabled={collapsingInactive}>
              {collapsingInactive ? 'Sending...' : 'Propose Collapse + Redeal'}
            </button>
          )}
        </div>
        {collapsePanel}
      </div>
    );
  }

  return (
    <div className="swap-phase">
      <h2>Swap Phase</h2>
      <p className="swap-instructions">
        You may exchange any hand card with one of your face-up cards.<br />
        Click <strong>Ready</strong> when you're satisfied.
      </p>

      <div className="swap-selection">
        <FaceUpCards
          cards={myState?.faceUp || []}
          swapPhase
          selectedId={selectedFaceUp}
          onSelect={setSelectedFaceUp}
        />
        <PlayerHand
          cards={myState?.hand || []}
          swapPhase
          selectedId={selectedHand}
          onSelect={setSelectedHand}
        />
      </div>

      <div className="swap-actions">
        <button onClick={handleSwap} disabled={!canSwap} className="btn-swap">
          Swap Selected
        </button>
        <button onClick={onReady} className="btn-ready">
          Ready ✓
        </button>
      </div>

      {waitingOnNames.length > 0 && (
        <p className="admin-note">Waiting on: {waitingOnNames.join(', ')}</p>
      )}

      <div className="swap-actions" style={{ marginTop: '0.4rem' }}>
        <button onClick={onBackToWaitingRoom} className="btn-cancel" type="button">
          Back To Waiting Room (Keep Seat)
        </button>
        {canManageWaitingRoom && (
          <button onClick={onCollapseInactive} className="btn-cancel" type="button" disabled={collapsingInactive}>
            {collapsingInactive ? 'Sending...' : 'Propose Collapse + Redeal'}
          </button>
        )}
      </div>

      {collapsePanel}

      <p className="swap-ready-count">{readyCount}/{totalCount} players ready</p>
    </div>
  );
};

export default SwapPhase;
